// @ts-check

/**
 * @typedef {object} Stockist
 * @property {string} name
 * @property {string} address
 * @property {string} [zip]
 * @property {string} city
 * @property {number} lat
 * @property {number} lng
 */

/** @type {Promise<any> | undefined} */
let mapsApiPromise;

/** @type {Set<() => void>} */
const authFailureHandlers = new Set();

/**
 * Great-circle distance in kilometres, so ranking needs no extra Google library.
 * @param {{ lat: number, lng: number }} from
 * @param {{ lat: number, lng: number }} to
 * @returns {number}
 */
function distanceInKm(from, to) {
  const earthRadius = 6371;
  /** @param {number} value */
  const toRadians = (value) => (value * Math.PI) / 180;
  const deltaLat = toRadians(to.lat - from.lat);
  const deltaLng = toRadians(to.lng - from.lng);
  const a =
    Math.sin(deltaLat / 2) ** 2 +
    Math.cos(toRadians(from.lat)) * Math.cos(toRadians(to.lat)) * Math.sin(deltaLng / 2) ** 2;
  return earthRadius * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Google calls this global when it refuses the API key, which happens after the
 * map is already constructed. It replaces Google's untranslated error overlay.
 */
/** @type {any} */ (window).gm_authFailure = () => {
  for (const handler of authFailureHandlers) handler();
};

/**
 * Loads the Google Maps JavaScript API once and resolves with the `google` global.
 * @param {string} apiKey
 * @returns {Promise<any>}
 */
function loadGoogleMaps(apiKey) {
  if (!mapsApiPromise) {
    mapsApiPromise = new Promise((resolve) => {
      const w = /** @type {any} */ (window);
      w.__stockistsMapsReady = () => resolve(w.google);
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&callback=__stockistsMapsReady&loading=async`;
      script.async = true;
      document.head.appendChild(script);
    });
  }
  return mapsApiPromise;
}

/**
 * Resellers list synced with a Google Map: clicking a list item focuses its
 * marker and clicking a marker highlights its list item.
 */
class StockistsMap extends HTMLElement {
  /** @type {any} */
  #map;
  /** @type {any} */
  #google;
  /** @type {any[]} */
  #markers = [];
  /** @type {any} */
  #infoWindow;
  /** @type {any} */
  #geocoder;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #searchTimer;
  /** Guards against a slow geocode result overwriting a newer one. */
  #searchToken = 0;
  /** @type {Stockist[]} */
  #stockists = [];
  /** @type {AbortController | undefined} */
  #abortController;
  /** @type {(() => void) | undefined} */
  #authFailureHandler;
  #scrollArrowObserver = new ResizeObserver(() => this.#updateScrollArrow());

  connectedCallback() {
    this.#abortController = new AbortController();
    this.#stockists = this.#readStockists();

    this.#authFailureHandler = () => this.#showMapError();
    authFailureHandlers.add(this.#authFailureHandler);

    this.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const button = target.closest('[data-index]');
        if (!(button instanceof HTMLElement)) return;
        const index = Number(button.dataset.index);
        if (!Number.isNaN(index)) this.#select(index, { scrollList: false });
      },
      { signal: this.#abortController.signal }
    );

    const search = this.querySelector('[data-stockists-search]');
    search?.addEventListener(
      'input',
      (event) => this.#handleSearchInput(/** @type {HTMLInputElement} */ (event.target).value),
      { signal: this.#abortController.signal }
    );

    // Submitting skips the debounce so Enter still works.
    this.querySelector('[data-stockists-search-form]')?.addEventListener(
      'submit',
      (event) => {
        event.preventDefault();
        clearTimeout(this.#searchTimer);
        if (search instanceof HTMLInputElement) this.#searchAddress(search.value);
      },
      { signal: this.#abortController.signal }
    );

    const viewport = this.querySelector('[data-stockists-list-viewport]');
    if (viewport) {
      viewport.addEventListener('scroll', () => this.#updateScrollArrow(), {
        signal: this.#abortController.signal,
        passive: true,
      });
      // Watch the viewport and its content: either resizing changes whether
      // there is anything left to scroll.
      this.#scrollArrowObserver.observe(viewport);
      if (viewport.firstElementChild) this.#scrollArrowObserver.observe(viewport.firstElementChild);
    }
    this.#updateScrollArrow();

    const apiKey = this.dataset.apiKey;
    if (apiKey && this.#stockists.length) {
      this.#initMap(apiKey);
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    clearTimeout(this.#searchTimer);
    this.#scrollArrowObserver.disconnect();
    if (this.#authFailureHandler) authFailureHandlers.delete(this.#authFailureHandler);
  }

  /**
   * Replaces the map with a readable message when Google refuses the API key.
   */
  #showMapError() {
    const container = this.querySelector('[data-stockists-map]');
    if (!(container instanceof HTMLElement)) return;

    container.removeAttribute('style');
    const message = document.createElement('p');
    message.className = 'stockists__map-placeholder';
    message.textContent = this.dataset.mapErrorText ?? '';
    container.replaceChildren(message);

    console.error(
      '[stockists] Google Maps refused the API key. Check the Google error logged above, then verify in the Google Cloud Console that the Maps JavaScript API is enabled and that this domain is allowed by the key restrictions.'
    );
  }

  /**
   * @returns {Stockist[]}
   */
  #readStockists() {
    const dataElement = this.querySelector('[data-stockists-data]');
    if (!dataElement?.textContent) return [];

    try {
      const parsed = JSON.parse(dataElement.textContent);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .map((item) => ({ ...item, lat: Number(item.lat), lng: Number(item.lng) }))
        .filter((item) => Number.isFinite(item.lat) && Number.isFinite(item.lng));
    } catch {
      return [];
    }
  }

  /**
   * @param {string} apiKey
   */
  async #initMap(apiKey) {
    const container = this.querySelector('[data-stockists-map]');
    if (!(container instanceof HTMLElement)) return;

    const google = await loadGoogleMaps(apiKey);
    if (!this.isConnected) return;
    this.#google = google;

    const { Map } = await google.maps.importLibrary('maps');
    /** @type {any} */
    let AdvancedMarkerElement;
    try {
      ({ AdvancedMarkerElement } = await google.maps.importLibrary('marker'));
    } catch {
      // Handled by #createMarker, which falls back to the classic marker.
    }
    if (!this.isConnected) return;

    container.querySelector('.stockists__map-placeholder')?.remove();

    this.#map = new Map(container, {
      // Advanced markers only render on a map created with a map ID. DEMO_MAP_ID
      // keeps the markers working before a merchant creates their own.
      mapId: this.dataset.mapId || 'DEMO_MAP_ID',
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoom: 8,
      center: { lat: this.#stockists[0]?.lat ?? 0, lng: this.#stockists[0]?.lng ?? 0 },
    });
    // headerDisabled removes Google's 48px header row, which otherwise holds
    // nothing but an oversized close button and pushes the content down.
    this.#infoWindow = new google.maps.InfoWindow({ headerDisabled: true, maxWidth: 260 });
    this.#map.addListener('click', () => this.#infoWindow.close());
    this.#geocoder = new google.maps.Geocoder();

    const bounds = new google.maps.LatLngBounds();
    this.#markers = this.#stockists.map((stockist, index) => {
      const position = { lat: stockist.lat, lng: stockist.lng };
      bounds.extend(position);
      const marker = this.#createMarker(google, AdvancedMarkerElement, position, stockist.name);
      marker.addListener('click', () => this.#select(index, { scrollList: true }));
      return marker;
    });

    if (this.#stockists.length > 1) {
      this.#map.fitBounds(bounds, 48);
    } else {
      this.#map.setZoom(14);
    }
  }

  /**
   * Creates an advanced marker, falling back to the classic marker when the
   * advanced one is unavailable so the map never renders without its pins.
   * @param {any} google
   * @param {any} AdvancedMarkerElement
   * @param {{ lat: number, lng: number }} position
   * @param {string} title
   * @returns {any}
   */
  #createMarker(google, AdvancedMarkerElement, position, title) {
    if (AdvancedMarkerElement) {
      try {
        return new AdvancedMarkerElement({ map: this.#map, position, title, gmpClickable: true });
      } catch (error) {
        console.warn('[stockists] Advanced markers unavailable, using classic markers instead.', error);
      }
    }
    return new google.maps.Marker({ map: this.#map, position, title });
  }

  /**
   * Highlights a stockist in the list and focuses its marker on the map.
   * @param {number} index
   * @param {{ scrollList?: boolean }} options
   */
  #select(index, options = {}) {
    for (const item of this.querySelectorAll('[data-stockists-item]')) {
      const isActive = Number(/** @type {HTMLElement} */ (item).dataset.stockistsItem) === index;
      item.classList.toggle('stockists__item--active', isActive);
      const button = item.querySelector('[data-index]');
      if (isActive) {
        button?.setAttribute('aria-current', 'true');
        if (options.scrollList) item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      } else {
        button?.removeAttribute('aria-current');
      }
    }

    const marker = this.#markers[index];
    const stockist = this.#stockists[index];
    if (!this.#map || !marker || !stockist) return;

    this.#map.panTo({ lat: stockist.lat, lng: stockist.lng });
    if (this.#map.getZoom() < 12) this.#map.setZoom(12);
    this.#infoWindow.setContent(
      `<div class="stockists__popup">
        <p class="stockists__popup-name">${this.#escapeHtml(stockist.name)}</p>
        <p class="stockists__popup-address">${this.#escapeHtml(stockist.address)}, ${this.#escapeHtml(
        [stockist.zip, stockist.city].filter(Boolean).join(' ')
      )}</p>
      </div>`
    );
    this.#infoWindow.open({ map: this.#map, anchor: marker });
  }

  /**
   * Filters instantly while typing, then looks the address up once typing
   * settles so the ranking appears without needing to submit.
   * @param {string} value
   */
  #handleSearchInput(value) {
    clearTimeout(this.#searchTimer);
    const query = value.trim();

    // Too short to be an address, and geocoding every keystroke would be waste.
    const willGeocode = Boolean(this.#geocoder) && query.length >= 4;

    // Stay quiet about no matches while a lookup is about to replace them.
    this.#filter(query, { quiet: willGeocode });

    if (willGeocode) {
      this.#searchTimer = setTimeout(() => this.#searchAddress(query), 500);
    }
  }

  /**
   * Filters the list and the map markers on a free-text query, then refits the
   * map to the matches.
   * @param {string} query
   * @param {{ quiet?: boolean }} [options]
   */
  #filter(query, options = {}) {
    const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const bounds = this.#google ? new this.#google.maps.LatLngBounds() : null;
    let matches = 0;

    this.#clearDistances();
    this.#restoreOrder();

    this.#stockists.forEach((stockist, index) => {
      const haystack = [stockist.name, stockist.address, stockist.zip, stockist.city]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      const isMatch = terms.every((term) => haystack.includes(term));

      this.#itemFor(index)?.toggleAttribute('hidden', !isMatch);
      const marker = this.#markers[index];
      if (marker) this.#setMarkerVisible(marker, isMatch);

      if (isMatch) {
        matches += 1;
        bounds?.extend({ lat: stockist.lat, lng: stockist.lng });
      }
    });

    this.#setMessage(matches > 0 || options.quiet ? '' : this.dataset.noResultsText ?? '');
    if (this.#map && bounds && matches > 0) {
      this.#infoWindow?.close();
      if (matches > 1) this.#map.fitBounds(bounds, 48);
      else this.#map.setCenter(bounds.getCenter());
    }
    this.#updateScrollArrow();
  }

  /**
   * Geocodes the query and ranks the stockists by distance from it. Falls back to
   * the plain text filter when the address cannot be resolved.
   * @param {string} query
   */
  async #searchAddress(query) {
    const trimmed = query.trim();
    if (!trimmed || !this.#geocoder) {
      this.#filter(trimmed);
      return;
    }

    const token = ++this.#searchToken;
    let location;
    try {
      const { results } = await this.#geocoder.geocode({ address: trimmed });
      location = results?.[0]?.geometry?.location;
    } catch {
      // Covers ZERO_RESULTS as well as the Geocoding API being unavailable.
    }
    // A newer query started while this one was in flight.
    if (!this.isConnected || token !== this.#searchToken) return;

    if (!location) {
      this.#filter(trimmed);
      this.#setMessage(this.dataset.addressNotFoundText ?? '');
      return;
    }

    this.#rankByDistance({ lat: location.lat(), lng: location.lng() });
  }

  /**
   * Reorders the list nearest first, labels each entry with its distance and
   * frames the map on the search point.
   * @param {{ lat: number, lng: number }} origin
   */
  #rankByDistance(origin) {
    const list = this.querySelector('[data-stockists-list]');
    const template = this.dataset.distanceText || '[distance] km';
    const ranked = this.#stockists
      .map((stockist, index) => ({ index, stockist, distance: distanceInKm(origin, stockist) }))
      .sort((a, b) => a.distance - b.distance);

    for (const { index, distance } of ranked) {
      const item = this.#itemFor(index);
      if (!item) continue;

      item.removeAttribute('hidden');
      list?.appendChild(item);

      const label = item.querySelector('[data-stockists-distance]');
      if (label) {
        const rounded = distance < 10 ? distance.toFixed(1) : String(Math.round(distance));
        label.textContent = template.replace('[distance]', rounded);
        label.removeAttribute('hidden');
      }

      const marker = this.#markers[index];
      if (marker) this.#setMarkerVisible(marker, true);
    }

    this.#setMessage('');
    this.#updateScrollArrow();

    if (this.#map && this.#google) {
      const bounds = new this.#google.maps.LatLngBounds();
      bounds.extend(origin);
      for (const { stockist } of ranked.slice(0, 3)) {
        bounds.extend({ lat: stockist.lat, lng: stockist.lng });
      }
      this.#map.fitBounds(bounds, 48);
    }

    const nearest = ranked[0];
    if (nearest) this.#select(nearest.index, { scrollList: true });
  }

  /**
   * @param {number} index
   * @returns {HTMLElement | null}
   */
  #itemFor(index) {
    return this.querySelector(`[data-stockists-item="${index}"]`);
  }

  /**
   * Puts the list back in its original order.
   */
  #restoreOrder() {
    const list = this.querySelector('[data-stockists-list]');
    if (!list) return;
    for (let index = 0; index < this.#stockists.length; index += 1) {
      const item = this.#itemFor(index);
      if (item) list.appendChild(item);
    }
  }

  #clearDistances() {
    for (const label of this.querySelectorAll('[data-stockists-distance]')) {
      label.textContent = '';
      label.setAttribute('hidden', '');
    }
  }

  /**
   * @param {string} text
   */
  #setMessage(text) {
    const element = this.querySelector('[data-stockists-no-results]');
    if (!element) return;
    element.textContent = text;
    element.toggleAttribute('hidden', !text);
  }

  /**
   * @param {any} marker
   * @param {boolean} visible
   */
  #setMarkerVisible(marker, visible) {
    const target = visible ? this.#map : null;
    if (typeof marker.setMap === 'function') marker.setMap(target);
    else marker.map = target;
  }

  /**
   * Shows the scroll cue while the list has more content below the fold.
   */
  #updateScrollArrow() {
    const viewport = this.querySelector('[data-stockists-list-viewport]');
    const arrow = this.querySelector('[data-stockists-scroll-arrow]');
    if (!(viewport instanceof HTMLElement) || !(arrow instanceof HTMLElement)) return;

    const remaining = viewport.scrollHeight - viewport.clientHeight - viewport.scrollTop;
    arrow.classList.toggle('stockists__scroll-arrow--visible', remaining > 8);
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }
}

if (!customElements.get('stockists-map')) {
  customElements.define('stockists-map', StockistsMap);
}
